import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, ChevronRight, ChevronLeft, MapPin, Crosshair, Quote, Shield, MessageSquare, Eye, Zap, Globe, FileText, Users, Handshake, Megaphone, Star, Calculator, DollarSign, Plane, Bell } from 'lucide-react';
import axios from 'axios';
import ListingCard from '../components/ListingCard';
import { useAuth } from '../contexts/AuthContext';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: 'ultraleggeri', name: 'Ultraleggeri', image: '/images/categorie/ultraleggeri.png' },
  { slug: 'propulsione-singola', name: 'Propulsione Singola', image: '/images/categorie/propulsione-singola.jpg' },
  { slug: 'propulsione-multipla', name: 'Propulsione Multipla', image: '/images/categorie/propulsione-multipla.jpg' },
  { slug: 'turboelica', name: 'Turboelica', image: '/images/categorie/turboelica.png' },
  { slug: 'jet', name: 'Jet', image: '/images/categorie/jet.jpg' },
  { slug: 'elicotteri', name: 'Elicotteri', image: '/images/categorie/elicotteri.png', imgPos: 'object-[center_70%]' },
];

const REVIEWS = [
  { name: 'Andrea R.', aircraft: 'ICP Savannah', text: 'Ho venduto il mio Savannah in poche settimane grazie all\'annuncio inserito su AeroDeals. La piattaforma e semplice da usare e ho ricevuto molteplici contatti in breve tempo.' },
  { name: 'Luigi B.', aircraft: 'Piper PA-28 Archer II', text: 'Piattaforma affidabile e molto visibile: ho ricevuto molte richieste ed inoltre il team è stato sempre disponibile per assistermi. Il mio Piper ha trovato un nuovo proprietario in tempi abbastanza brevi.' },
  { name: 'Alessandro M.', aircraft: 'Tecnam P92 Echo', text: 'Non pensavo di concludere cosi in fretta: ho pubblicato l\'annuncio e in meno di un mese ho trovato l\'acquirente. Ottimo servizio, lo consiglio a chiunque voglia vendere un ultraleggero.' },
  { name: 'Stefano M.', aircraft: 'Cessna 172 Skyhawk', text: 'Con AeroDeals ho raggiunto un pubblico che non avrei mai trovato altrove. Ho ricevuto proposte concrete e ho venduto il mio Cessna senza intermediari, in totale sicurezza.' },
  { name: 'Luca A.', aircraft: 'Robinson R44 Raven II', text: 'Vendere un elicottero non è semplice, ma con AeroDeals ho ricevuto contatti seri da professionisti e operatori del settore. Sono rimasto molto soddisfatto, consiglio.' },
];

const FEATURES = [
  { icon: Zap, title: 'Pubblica in 60 secondi', desc: 'Inserisci il tuo annuncio con pochi clic: informazioni, immagini e pacchetto di visibilità.' },
  { icon: MessageSquare, title: 'Messaggistica Integrata', desc: 'Comunica in sicurezza con la messaggistica integrata. Tutela la tua privacy ed evita lo spam.' },
  { icon: Eye, title: 'Alta Visibilità', desc: 'Annunci ottimizzati visibili a centinaia di potenziali acquirenti in Italia ed Europa.' },
  { icon: Shield, title: 'Revisione Manuale', desc: 'Ogni annuncio viene revisionato e ottimizzato dal nostro team entro 24h.' },
  { icon: Bell, title: 'Deal Alerts', desc: 'Salva le tue ricerche preferite e ricevi una notifica non appena viene pubblicato un annuncio che corrisponde ai tuoi criteri.' },
  { icon: Star, title: 'Contatti Qualificati', desc: 'Ricevi contatti seri e proposte concrete da professionisti e operatori del settore.' },
];

const SERVICES = [
  { icon: DollarSign, title: 'Aircraft Price Estimator', desc: 'Scopri il valore reale del tuo velivolo. Il nostro strumento analizza dati di mercato, ore di volo, anno di costruzione e condizioni per fornirti una stima accurata e aggiornata. Ideale per venditori che vogliono posizionarsi al prezzo giusto.', cta: 'Valuta il tuo velivolo', highlight: true },
  { icon: Calculator, title: 'Operating Cost Calculator', desc: 'Calcola i costi operativi reali prima di acquistare. Carburante, manutenzione, assicurazione, hangaraggio e revisioni: ottieni una panoramica completa dei costi annuali per ogni tipo di velivolo. Lo strumento perfetto per acquirenti seri.', cta: 'Calcola i costi', highlight: true },
  { icon: Users, title: 'Consulenze', desc: 'Supporto professionale per ogni fase della compravendita, dall\'analisi del velivolo alla strategià di negoziazione. Le nostre consulenze aiutano a prendere decisioni rapide e sicure, evitando errori costosi.', cta: 'Richiedi consulenza', link: '/consulenze-compravendita-velivoli' },
  { icon: FileText, title: 'Lettera di Intenti', desc: 'Formalizza in modo semplice e sicuro l\'intenzione di acquistare o vendere un velivolo. Definisci prezzo, condizioni e periodi di esclusiva prima della due diligence, tutelando entrambe le parti.', cta: 'Scarica il PDF', link: '/lettera-di-intenti-compravendita-velivolo-tra-privati' },
  { icon: Search, title: 'Piani di Acquisizione', desc: 'Analizziamo il mercato e definiamo una strategià su misura per l\'acquisto del tuo velivolo. I Piani di Acquisizione aiutano a identificare opportunità, valutare il prezzo giusto e ridurre i rischi.', cta: 'Richiedi il piano', link: '/piano-di-acquisizione-velivolo' },
  { icon: Megaphone, title: 'Spazio Pubblicitario', desc: 'Promuovi la tua azienda con banner premium in homepage per ottenere massima visibilità nel settore aeronautico.', cta: 'Richiedi il media kit', link: '/spazio-pubblicitario' },
];

export default function Home() {
  useSEO({
    title: 'AeroDeals — Marketplace Aeronautico Italiano: Aerei, Ultraleggeri ed Elicotteri in Vendita',
    description: 'AeroDeals è il marketplace italiano n°1 per la compravendita di aeromobili: ultraleggeri, aerei a propulsione singola e multipla, turboelica, jet ed elicotteri usati e nuovi. Annunci verificati, messaggistica sicura e servizi professionali.',
    keywords: 'aerei usati, ultraleggeri in vendita, elicotteri usati, jet privati, marketplace aeronautico, compravendita aerei, aerodeals',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'AeroDeals',
      url: 'https://www.aerodeals.it/',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.aerodeals.it/listings?search={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  });
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationText, setLocationText] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [showLocResults, setShowLocResults] = useState(false);
  const [distance, setDistance] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [heroCategory, setHeroCategory] = useState('');
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [locating, setLocating] = useState(false);
  const [reviewIdx, setReviewIdx] = useState(0);
  const reviewInterval = useRef(null);
  const locTimerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/listings/featured`).then(r => setFeatured(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/favorites/ids`, { withCredentials: true }).then(r => setFavoriteIds(r.data)).catch(() => {});
    }
  }, [user]);

  const toggleFavorite = async (listingId) => {
    if (!user) return;
    await axios.post(`${API}/favorites/${listingId}`, {}, { withCredentials: true });
    setFavoriteIds(prev => prev.includes(listingId) ? prev.filter(id => id !== listingId) : [...prev, listingId]);
  };

  // Auto-scroll reviews
  useEffect(() => {
    reviewInterval.current = setInterval(() => {
      setReviewIdx(prev => (prev + 1) % REVIEWS.length);
    }, 5000);
    return () => clearInterval(reviewInterval.current);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('search', searchQuery);
    if (heroCategory) params.set('category', heroCategory);
    if (maxPrice) params.set('max_price', maxPrice);
    if (locationText) params.set('location', locationText);
    if (distance && userLat && userLng) {
      params.set('distance', distance);
      params.set('lat', userLat);
      params.set('lng', userLng);
    }
    window.location.href = `/listings?${params.toString()}`;
  };

  const searchLocation = (q) => {
    setLocationText(q);
    if (locTimerRef.current) clearTimeout(locTimerRef.current);
    if (q.length < 2) { setLocationResults([]); setShowLocResults(false); return; }
    locTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/places/autocomplete`, { params: { q } });
        setLocationResults(res.data);
        setShowLocResults(true);
      } catch { setLocationResults([]); }
    }, 300);
  };

  const selectLocation = (r) => {
    setLocationText(r.description.split(',')[0]);
    if (r.lat != null && r.lng != null) {
      setUserLat(r.lat);
      setUserLng(r.lng);
    }
    setShowLocResults(false);
  };

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        try {
          const resp = await axios.get(`https://nominatim.openstreetmap.org/reverse`, {
            params: { lat: pos.coords.latitude, lon: pos.coords.longitude, format: 'json', 'accept-language': 'it' },
            headers: { 'User-Agent': 'AeroDeals/1.0' }
          });
          const city = resp.data?.address?.city || resp.data?.address?.town || resp.data?.address?.village || '';
          setLocationText(city || `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
        } catch {
          setLocationText(`${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
        }
        setLocating(false);
      },
      () => { setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleDistanceFocus = () => {
    if (!userLat && !userLng) {
      handleLocate();
    }
  };

  const prevReview = () => { clearInterval(reviewInterval.current); setReviewIdx(prev => (prev - 1 + REVIEWS.length) % REVIEWS.length); };
  const nextReview = () => { clearInterval(reviewInterval.current); setReviewIdx(prev => (prev + 1) % REVIEWS.length); };

  return (
    <div data-testid="home-page">
      {/* ─── Hero Section ─── */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="hero-gradient absolute inset-0 z-10" />
          <div className="absolute inset-0 z-[5] bg-gradient-to-r from-[#09090B] via-[#09090B]/70 to-[#09090B]/40" />
          <img src="https://images.unsplash.com/photo-1721998035122-68d9bf2c0cd2?w=1920&h=1080&fit=crop" alt="Private jet" className="w-full h-full object-cover object-[80%_center]" />
        </div>

        {/* 3D Floating Elements - Right Side */}
        <div className="absolute right-0 top-0 w-1/2 h-full z-[11] pointer-events-none hidden lg:block hero-3d-container" data-testid="hero-3d-elements">
          {/* Glowing orbs */}
          <div className="absolute top-[18%] right-[12%] w-44 h-44 rounded-full bg-[#C5A059]/15 hero-3d-glow" />
          <div className="absolute top-[55%] right-[30%] w-28 h-28 rounded-full bg-[#C5A059]/10 hero-3d-glow" style={{ animationDelay: '2s' }} />

          {/* Orbiting rings */}
          <div className="absolute top-[25%] right-[18%] w-32 h-32 border border-[#C5A059]/15 rounded-full hero-3d-orbit" />
          <div className="absolute top-[25%] right-[18%] w-20 h-20 border border-zinc-500/20 rounded-full hero-3d-orbit" style={{ animationDuration: '14s', animationDirection: 'reverse' }} />

          <div className="absolute top-[58%] right-[10%] w-24 h-24 border border-[#C5A059]/12 rounded-full hero-3d-orbit" style={{ animationDuration: '18s' }} />
          <div className="absolute top-[58%] right-[10%] w-14 h-14 border border-zinc-600/20 rounded-full hero-3d-orbit" style={{ animationDuration: '12s', animationDirection: 'reverse' }} />

          {/* Floating dots */}
          <div className="hero-3d-float-slow absolute top-[12%] right-[30%] w-2 h-2 bg-[#C5A059] rounded-full opacity-60" />
          <div className="hero-3d-float absolute top-[40%] right-[8%] w-2.5 h-2.5 bg-[#C5A059] rounded-full opacity-50" />
          <div className="hero-3d-float-reverse absolute top-[72%] right-[22%] w-2 h-2 bg-[#C5A059] rounded-full opacity-40" />
          <div className="hero-3d-float absolute top-[85%] right-[12%] w-1.5 h-1.5 bg-zinc-400 rounded-full opacity-30" />
          <div className="hero-3d-float-slow absolute top-[32%] right-[40%] w-1.5 h-1.5 bg-[#C5A059] rounded-full opacity-35" />

          {/* Line accents */}
          <div className="absolute top-[35%] right-[5%] w-24 h-px bg-gradient-to-l from-[#C5A059]/30 to-transparent hero-3d-float-slow" />
          <div className="absolute top-[65%] right-[35%] w-16 h-px bg-gradient-to-r from-[#C5A059]/25 to-transparent hero-3d-float" />

          {/* Diamond shapes */}
          <div className="hero-3d-float-reverse absolute top-[20%] right-[38%] w-4 h-4 border border-[#C5A059]/25 rotate-45 opacity-50" />
          <div className="hero-3d-float absolute top-[50%] right-[5%] w-3 h-3 border border-zinc-500/30 rotate-45 opacity-40" />
          <div className="hero-3d-float-slow absolute top-[78%] right-[38%] w-5 h-5 border border-[#C5A059]/20 rotate-45 opacity-35" />

          {/* Cross marks */}
          <div className="hero-3d-float absolute top-[45%] right-[42%] opacity-20">
            <div className="w-4 h-px bg-[#C5A059] absolute top-1/2 left-0" />
            <div className="w-px h-4 bg-[#C5A059] absolute left-1/2 top-0" />
          </div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 w-full">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-6 animate-fade-in">
              Il Nuovo Marketplace del Cielo
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 animate-fade-in-delay-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Compra e Vendi<br />
              <span className="text-[#C5A059]">Ultraleggeri, Aerei<br />ed Elicotteri</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-10 max-w-xl animate-fade-in-delay-2">
              Sei un proprietario o rivenditore?<br />Inserisci gratuitamente l'annuncio di vendita.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="animate-fade-in-delay-3">
              <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-700 overflow-hidden max-w-2xl">
                {/* Row 1: Search (cols 1-2) + Location (cols 3-4) */}
                <div className="grid grid-cols-1 sm:grid-cols-4">
                  <div className="sm:col-span-2 flex items-center px-4 gap-3 border-b sm:border-b-0 sm:border-r border-zinc-700/50">
                    <Search size={16} className="text-zinc-500 shrink-0" />
                    <input
                      type="text"
                      placeholder="Cerca per modello, marca o tipo..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full py-3 bg-transparent text-white placeholder-zinc-500 outline-none text-sm"
                      data-testid="hero-search-input"
                    />
                  </div>
                  <div className="sm:col-span-2 flex items-center px-4 gap-3 relative">
                    <MapPin size={16} className="text-zinc-500 shrink-0" />
                    <input
                      type="text"
                      placeholder="Posizione..."
                      value={locationText}
                      onChange={e => searchLocation(e.target.value)}
                      onFocus={() => locationResults.length > 0 && setShowLocResults(true)}
                      onBlur={() => setTimeout(() => setShowLocResults(false), 200)}
                      className="w-full py-3 bg-transparent text-white placeholder-zinc-500 outline-none text-sm min-w-0"
                      data-testid="hero-location-input"
                    />
                    <button
                      type="button"
                      onClick={handleLocate}
                      className="shrink-0 text-[#C5A059] hover:text-[#D4B271] transition-colors p-1"
                      title="Localizzami"
                      data-testid="locate-me-btn"
                    >
                      <Crosshair size={16} className={locating ? 'animate-spin' : ''} />
                    </button>
                    {showLocResults && locationResults.length > 0 && (
                      <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-zinc-900 border border-zinc-700 max-h-48 overflow-auto">
                        {locationResults.map((r, i) => (
                          <button key={i} type="button" onMouseDown={() => selectLocation(r)} className="block w-full text-left px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white border-b border-zinc-800 last:border-0">
                            <MapPin size={12} className="inline mr-2 text-[#C5A059]" />{r.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Row 2: Category | Price | Distance | Cerca (4 equal cols → middle divider aligns with Row 1) */}
                <div className="grid grid-cols-1 sm:grid-cols-4 border-t border-zinc-700/50">
                  <div className="flex items-center gap-3 px-4 py-3 border-b sm:border-b-0 sm:border-r border-zinc-700/50 min-w-0">
                    <Plane size={14} className="text-zinc-500 shrink-0" />
                    <select
                      value={heroCategory}
                      onChange={e => setHeroCategory(e.target.value)}
                      className="bg-transparent text-sm text-zinc-300 outline-none w-full cursor-pointer min-w-0"
                      data-testid="hero-category-filter"
                    >
                      <option value="" className="bg-zinc-900">Categoria</option>
                      <option value="ultraleggeri" className="bg-zinc-900">Ultraleggeri</option>
                      <option value="propulsione-singola" className="bg-zinc-900">Propulsione Singola</option>
                      <option value="propulsione-multipla" className="bg-zinc-900">Propulsione Multipla</option>
                      <option value="turboelica" className="bg-zinc-900">Turboelica</option>
                      <option value="jet" className="bg-zinc-900">Jet</option>
                      <option value="elicotteri" className="bg-zinc-900">Elicotteri</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 px-4 py-3 border-b sm:border-b-0 sm:border-r border-zinc-700/50 min-w-0">
                    <span className="text-sm text-zinc-500 shrink-0">€</span>
                    <select
                      value={maxPrice}
                      onChange={e => setMaxPrice(e.target.value)}
                      className="bg-transparent text-sm text-zinc-300 outline-none w-full cursor-pointer min-w-0"
                      data-testid="price-filter"
                    >
                      <option value="" className="bg-zinc-900">Qualsiasi</option>
                      <option value="30000" className="bg-zinc-900">€30.000</option>
                      <option value="50000" className="bg-zinc-900">€50.000</option>
                      <option value="100000" className="bg-zinc-900">€100.000</option>
                      <option value="250000" className="bg-zinc-900">€250.000</option>
                      <option value="500000" className="bg-zinc-900">€500.000</option>
                      <option value="1000000" className="bg-zinc-900">€1.000.000</option>
                      <option value="5000000" className="bg-zinc-900">€5.000.000</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 px-4 py-3 border-b sm:border-b-0 sm:border-r border-zinc-700/50 min-w-0">
                    <Crosshair size={14} className={`shrink-0 ${userLat ? 'text-[#C5A059]' : 'text-zinc-500'} ${locating ? 'animate-spin' : ''}`} />
                    <select
                      value={distance}
                      onChange={e => setDistance(e.target.value)}
                      onFocus={handleDistanceFocus}
                      className="bg-transparent text-sm text-zinc-300 outline-none w-full cursor-pointer min-w-0"
                      data-testid="distance-filter"
                    >
                      <option value="" className="bg-zinc-900">Distanza</option>
                      <option value="25" className="bg-zinc-900">25 km</option>
                      <option value="50" className="bg-zinc-900">50 km</option>
                      <option value="100" className="bg-zinc-900">100 km</option>
                      <option value="250" className="bg-zinc-900">250 km</option>
                      <option value="500" className="bg-zinc-900">500 km</option>
                    </select>
                  </div>
                  <button type="submit" className="bg-[#C5A059] text-black px-4 py-3 font-medium text-sm hover:bg-[#D4B271] transition-colors w-full" data-testid="hero-search-btn">
                    Cerca
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ─── Reviews Carousel ─── */}
      <section className="py-16 bg-zinc-950 border-b border-zinc-800/50" data-testid="reviews-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="text-center">
              <Quote size={28} className="text-[#C5A059]/30 mx-auto mb-6" />
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed italic min-h-[80px] transition-all duration-500" data-testid="review-text">
                "{REVIEWS[reviewIdx].text}"
              </p>
              <div className="mt-6">
                <p className="text-sm font-medium text-white" data-testid="review-name">{REVIEWS[reviewIdx].name}</p>
                <p className="text-xs text-[#C5A059] font-mono mt-1">{REVIEWS[reviewIdx].aircraft}</p>
              </div>
            </div>
            {/* Controls */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <button onClick={prevReview} className="w-8 h-8 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" data-testid="review-prev">
                <ChevronLeft size={16} />
              </button>
              <div className="flex gap-2">
                {REVIEWS.map((_, i) => (
                  <button key={i} onClick={() => { clearInterval(reviewInterval.current); setReviewIdx(i); }} className={`w-2 h-2 rounded-full transition-colors ${i === reviewIdx ? 'bg-[#C5A059]' : 'bg-zinc-700'}`} data-testid={`review-dot-${i}`} />
                ))}
              </div>
              <button onClick={nextReview} className="w-8 h-8 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" data-testid="review-next">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Categories Grid ─── */}
      <section className="py-20 bg-[#09090B]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Esplora</p>
              <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Categorie</h2>
            </div>
            <Link to="/listings" className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-[#C5A059] transition-colors" data-testid="view-all-listings-link">
              Tutti gli annunci <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map(cat => (
              <Link key={cat.slug} to={`/listings?category=${cat.slug}`} className="category-card group relative overflow-hidden border border-zinc-800 bg-zinc-900/30 p-6 flex items-end min-h-[160px]" data-testid={`category-${cat.slug}`}>
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                  <img src={cat.image} alt="" className={`w-full h-full object-cover ${cat.imgPos || ''}`} />
                </div>
                <div className="relative z-10 flex items-center justify-between w-full">
                  <h3 className="text-lg font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{cat.name}</h3>
                  <ChevronRight size={18} className="text-zinc-600 group-hover:text-[#C5A059] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Featured Listings ─── */}
      {featured.length > 0 && (
        <section className="py-20 bg-zinc-950" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Selezionati</p>
                <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Annunci In Evidenza</h2>
              </div>
              <Link to="/listings?featured=true" className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-[#C5A059] transition-colors">
                Vedi tutti <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.slice(0, 3).map(listing => (
                <ListingCard key={listing.listing_id} listing={listing} isFavorited={favoriteIds.includes(listing.listing_id)} onToggleFavorite={user ? toggleFavorite : undefined} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Come Funziona ─── */}
      <section className="py-20 bg-[#09090B] border-t border-zinc-800/50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">La Piattaforma</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Come funziona AeroDeals?</h2>
            <p className="text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              La piattaforma di AeroDeals semplifica le operazioni di compravendita di velivoli mettendo in contatto acquirenti e venditori di Ultraleggeri, Aerei ed Elicotteri, a livello internazionale in modo rapido e sicuro.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(f => (
              <div key={f.title} className="bg-zinc-900/30 border border-zinc-800 p-6 hover:border-zinc-600 transition-colors group" data-testid={`feature-card-${f.title.toLowerCase().replace(/\s/g, '-')}`}>
                <f.icon size={22} className="text-[#C5A059] mb-4 group-hover:scale-110 transition-transform" />
                <h4 className="text-sm font-medium text-white mb-2">{f.title}</h4>
                <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Servizi alla Compravendita ─── */}
      <section className="py-20 bg-zinc-950 border-t border-zinc-800/50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Supporto Completo</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Servizi alla Compravendita</h2>
            <p className="text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              AeroDeals è più di un marketplace: offriamo servizi completi per supportare ogni fase della compravendita di velivoli. Dalla consulenza alla documentazione, forniamo strumenti professionali per trattative sicure e trasparenti.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map(s => (
              <div key={s.title} className={`border p-8 transition-colors group ${s.highlight ? 'bg-[#C5A059]/[0.04] border-[#C5A059]/20 hover:border-[#C5A059]/50' : 'bg-zinc-900/40 border-zinc-800 hover:border-zinc-600'}`} data-testid={`service-${s.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className={`w-12 h-12 flex items-center justify-center mb-5 ${s.highlight ? 'bg-[#C5A059]/15' : 'bg-zinc-800'}`}>
                  <s.icon size={22} className="text-[#C5A059]" />
                </div>
                <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {s.title}
                  {s.highlight && <span className="text-[9px] font-mono uppercase tracking-widest bg-[#C5A059]/20 text-[#C5A059] px-2 py-0.5 rounded-sm">Beta</span>}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
                <Link to={s.link ? s.link : (s.highlight ? `/services/${s.title === 'Aircraft Price Estimator' ? 'price-estimator' : 'cost-calculator'}` : '/services')} className={`inline-flex items-center gap-1.5 text-xs mt-5 transition-colors ${s.highlight ? 'bg-[#C5A059] text-black px-4 py-2 font-medium hover:bg-[#D4B271]' : 'text-[#C5A059] hover:underline'}`}>
                  {s.cta || 'Scopri di piu'} <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Partners ─── */}
      <section className="py-20 bg-[#09090B] border-t border-zinc-800/50" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-3">Collaborazioni</p>
            <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>I nostri Partners</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* AOPA Italia */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-8 flex flex-col hover:border-[#C5A059]/40 transition-colors" data-testid="partner-aopa">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-zinc-950">
                  <img src="/partners/aopa.png" alt="AOPA Italia" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>AOPA Italia</h3>
                  <p className="text-xs text-[#C5A059] font-mono">Aircraft Owners and Pilots Association</p>
                </div>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Partnership con AOPA Italia, sezione di IAOPA, l'Associazione Mondiale dei Piloti dell'Aviazione Generale, Ultraleggeri e del Lavoro Aereo presente dal 1939 in ben 84 paesi: l'obiettivo è quello di difendere il diritto di volare che ciascun Pilota possiede, contrastando attivamente burocrazie ed abusi.
              </p>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3">
                AOPA Italia offre inoltre agli associati notevoli vantaggi in termini di benefits e scontistiche, nonché la possibilità di conseguire l'ICAO Language Proficiency Test riconosciuto da ENAC.
              </p>
              <a href="https://www.aopa.it/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#C5A059] mt-4 hover:underline">
                Visita AOPA Italia <ArrowRight size={12} />
              </a>
            </div>

            {/* ALMA Aviation Studio */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-8 flex flex-col hover:border-[#C5A059]/40 transition-colors" data-testid="partner-alma">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-zinc-950">
                  <img src="/partners/alma.png" alt="ALMA Aviation Studio" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>ALMA Aviation Studio</h3>
                  <p className="text-xs text-[#C5A059] font-mono">Produzioni & Comunicazione Aeronautica</p>
                </div>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Partnership con ALMA Aviation Studio, agenzia Milanese leader nel settore delle produzioni e comunicazione aeronautica.
              </p>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3">
                Se stai per mettere in vendita il tuo Aereo, Elicottero o Ultraleggero, o vuoi semplicemente immortalare ricordi, ALMA offre un servizio fotografico e video dedicato alla valorizzazione del tuo Velivolo. Potenzia ora l'efficacia del tuo Annuncio con il 20% di sconto!
              </p>
              <a href="https://www.almaaviationstudio.it/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#C5A059] mt-4 hover:underline">
                Visita ALMA Aviation Studio <ArrowRight size={12} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA Section ─── */}
      <section className="py-24 bg-zinc-950 border-t border-zinc-800/50" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Vendi subito il tuo velivolo
          </h2>
          <p className="text-zinc-400 text-base mb-8 max-w-lg mx-auto">
            Pubblica il tuo annuncio in meno di 60 secondi e raggiungi migliaia di potenziali acquirenti.
          </p>
          <Link to="/create-listing" className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3.5 text-sm font-medium hover:bg-[#D4B271] transition-colors" data-testid="cta-create-listing">
            Pubblica Annuncio <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
